import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';

import i18n from '@/settings/i18n';
import { pages } from '@/settings/pages';
import { adminApi } from '@/services/api';
import { useCapyValues } from '@/hooks/useCapyValues';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useTurnstile } from '@/hooks/useTurnstile';
import { sentryReportError } from '@/utils/sentryReportError';

import { RemindForm, Values } from '../Presentation';

export const RemindFormContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const { setCapyValues, createParamsWithCapyValues } = useCapyValues();
  const {
    turnstileError,
    isLoading: turnstileLoading,
    onSuccess: onTurnstileSuccess,
    onError: onTurnstileError,
    resetError: resetTurnstileError,
    createParamsWithTurnstile,
  } = useTurnstile();

  // 送信中フラグ（二重送信防止）
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Capyモード用（Capy廃止後に削除）
  const [capyLoading, setCapyLoading] = useState(true);
  const [capyError, setCapyError] = useState(false);

  /**
   * Capyパズル完了時のハンドラ
   * - setCapyValuesが呼ばれる = パズル完了
   * - パズル完了を検知して送信ボタンを活性化する
   */
  const handleCapyValuesChange: typeof setCapyValues = useCallback(
    (values) => {
      setCapyLoading(false);
      setCapyValues(values);
    },
    [setCapyValues],
  );

  /**
   * Feature Flag取得（Turnstile/Capy切り替え）
   * - useTurnstile: Turnstile有効（true=Turnstile）
   * - loading: Feature Flag取得中かどうか（true=Spinner表示、false=CAPTCHA表示）
   */
  const { enabled: useTurnstileFlag, loading: featureFlagLoading } =
    useFeatureFlag('password_reset_turnstile_enabled');

  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (values: Values) => {
      try {
        setIsSubmitting(true);
        setCapyError(false);
        resetTurnstileError();

        let data;

        if (useTurnstileFlag) {
          // Turnstileモード
          const result = createParamsWithTurnstile(values);
          if (!result.isValid) {
            setIsSubmitting(false);
            return;
          }
          data = result.data;
        } else {
          // Capyモード
          data = createParamsWithCapyValues(values);
        }

        await adminApi.sendVerificationMail({ data });
        navigate(pages.token.sent);
      } catch (err) {
        if (!axios.isAxiosError(err)) {
          return;
        }

        // 送信ボタン再活性化（再送信可能にする）
        setIsSubmitting(false);

        // Capyエラー（Capy廃止後に削除）
        if (!useTurnstileFlag && err.response?.status === 401) {
          setCapyError(true);
          return;
        }

        // 4xx, 5xx → 予期せぬエラーメッセージを表示
        sentryReportError(err);
        flashError(i18n.t('ui.error.unexpected'));
      }
    },
    [
      useTurnstileFlag,
      createParamsWithTurnstile,
      createParamsWithCapyValues,
      resetTurnstileError,
      flashError,
      navigate,
    ],
  );

  // 送信ボタン無効化条件（Turnstileバッジ/Capyパズル完了前 または 送信中）
  const isSubmitButtonDisabled = useTurnstileFlag
    ? turnstileLoading || isSubmitting
    : capyLoading || isSubmitting;

  const childProps = {
    isDisabled: isSubmitButtonDisabled,
    capyError,
    onSubmit,
    setCapyValues: handleCapyValuesChange,
    useTurnstile: useTurnstileFlag,
    turnstileError,
    onTurnstileSuccess,
    onTurnstileError,
    featureFlagLoading,
  };
  return <RemindForm {...childProps} />;
};
